import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Container,
  Heading,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  IconButton,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  VStack,
  HStack,
  Text,
  Input,
  Textarea,
  Select,
  FormControl,
  FormLabel,
  useToast,
  Spinner,
  Alert,
  AlertIcon,
  Flex,
  Spacer,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Divider,
  Checkbox,
  CheckboxGroup,
  Stack,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Switch,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Progress,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  useColorModeValue
} from '@chakra-ui/react';
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaEye,
  FaUsers,
  FaChartBar,
  FaChevronDown,
  FaTimes,
  FaCheck,
  FaClock,
  FaCalendarAlt,
  FaQuestionCircle,
  FaUserGraduate
} from 'react-icons/fa';
import { motion } from 'framer-motion';
import AuthContext from '../services/AuthContext';
import axiosInstance from '../services/axiosInstance';
import SidebarInstructor from '../shared/Sidebar3';

const QCMManagementPage = () => {
  const { user } = useContext(AuthContext);
  const [qcms, setQcms] = useState([]);
  const [courses, setCourses] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedQcm, setSelectedQcm] = useState(null);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [attempts, setAttempts] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    course: '',
    timeLimit: 60,
    passingScore: 70,
    isActive: true,
    allowMultipleAttempts: false,
    showResultsImmediately: true,
    randomizeQuestions: false,
    randomizeOptions: false,
    availableFrom: '',
    availableUntil: '',
    instructions: '',
    questions: [{ 
      questionText: '', 
      points: 1,
      options: [
        { text: '', isCorrect: false },
        { text: '', isCorrect: false }
      ] 
    }]
  });

  // Modal disclosures
  const { isOpen: isCreateOpen, onOpen: onCreateOpen, onClose: onCreateClose } = useDisclosure();
  const { isOpen: isEditOpen, onOpen: onEditOpen, onClose: onEditClose } = useDisclosure();
  const { isOpen: isAssignOpen, onOpen: onAssignOpen, onClose: onAssignClose } = useDisclosure();
  const { isOpen: isViewOpen, onOpen: onViewOpen, onClose: onViewClose } = useDisclosure();
  const { isOpen: isStatsOpen, onOpen: onStatsOpen, onClose: onStatsClose } = useDisclosure();
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  
  const toast = useToast();
  const primaryColor = '#F2C919';
  const secondaryColor = '#2C3E50';
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  useEffect(() => {
    fetchQCMs();
    fetchCourses();
  }, []);

  const fetchQCMs = async () => {
    try {
      const response = await axiosInstance.get('/qcm/tests');
      setQcms(response.data);
    } catch (error) {
      toast({
        title: 'خطأ',
        description: 'فشل في تحميل الاختبارات',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchCourses = async () => {
    try {
      const response = await axiosInstance.get('/courses/courses/me');
      setCourses(response.data);
    } catch (error) {
      console.error('Error fetching courses:', error);
    }
  };

  const fetchStudentsForCourse = async (courseId) => {
    try {
      const response = await axiosInstance.get(`/courses/${courseId}/students`);
      setStudents(response.data);
    } catch (error) {
      toast({
        title: 'خطأ',
        description: 'فشل في تحميل الطلاب',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const fetchQCMStatistics = async (qcmId) => {
    try {
      const [studentsResponse, attemptsResponse] = await Promise.all([
        axiosInstance.get(`/qcm/tests/${qcmId}/students`),
        axiosInstance.get(`/qcm/attempts/test/${qcmId}`)
      ]);
      
      setStatistics({
        students: studentsResponse.data,
        attempts: attemptsResponse.data
      });
    } catch (error) {
      toast({
        title: 'خطأ',
        description: 'فشل في تحميل الإحصائيات',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleCreateQCM = async () => {
    try {
      await axiosInstance.post('/qcm/tests', formData);
      toast({
        title: 'نجح',
        description: 'تم إنشاء الاختبار بنجاح',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      onCreateClose();
      fetchQCMs();
      resetForm();
    } catch (error) {
      toast({
        title: 'خطأ',
        description: error.response?.data?.message || 'فشل في إنشاء الاختبار',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleUpdateQCM = async () => {
    try {
      await axiosInstance.put(`/qcm/tests/${selectedQcm._id}`, formData);
      toast({
        title: 'نجح',
        description: 'تم تحديث الاختبار بنجاح',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      onEditClose();
      fetchQCMs();
      resetForm();
    } catch (error) {
      toast({
        title: 'خطأ',
        description: error.response?.data?.message || 'فشل في تحديث الاختبار',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleDeleteQCM = async () => {
    try {
      await axiosInstance.delete(`/qcm/tests/${selectedQcm._id}`);
      toast({
        title: 'نجح',
        description: 'تم حذف الاختبار بنجاح',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      onDeleteClose();
      fetchQCMs();
    } catch (error) {
      toast({
        title: 'خطأ',
        description: error.response?.data?.message || 'فشل في حذف الاختبار',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleAssignQCM = async () => {
    try {
      await axiosInstance.post(`/qcm/tests/${selectedQcm._id}/assign`, {
        studentIds: selectedStudents,
        maxAttempts: 1
      });
      toast({
        title: 'نجح',
        description: 'تم تعيين الاختبار للطلاب بنجاح',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      onAssignClose();
      setSelectedStudents([]);
    } catch (error) {
      toast({
        title: 'خطأ',
        description: error.response?.data?.message || 'فشل في تعيين الاختبار',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      course: '',
      timeLimit: 60,
      passingScore: 70,
      isActive: true,
      allowMultipleAttempts: false,
      showResultsImmediately: true,
      randomizeQuestions: false,
      randomizeOptions: false,
      availableFrom: '',
      availableUntil: '',
      instructions: '',
      questions: [{ 
        questionText: '', 
        points: 1,
        options: [
          { text: '', isCorrect: false },
          { text: '', isCorrect: false }
        ] 
      }]
    });
  };

  const addQuestion = () => {
    setFormData({
      ...formData,
      questions: [...formData.questions, { 
        questionText: '', 
        points: 1,
        options: [
          { text: '', isCorrect: false },
          { text: '', isCorrect: false }
        ] 
      }]
    });
  };

  const removeQuestion = (questionIndex) => {
    const newQuestions = formData.questions.filter((_, index) => index !== questionIndex);
    setFormData({ ...formData, questions: newQuestions });
  };

  const addOption = (questionIndex) => {
    const newQuestions = [...formData.questions];
    newQuestions[questionIndex].options.push({ text: '', isCorrect: false });
    setFormData({ ...formData, questions: newQuestions });
  };

  const removeOption = (questionIndex, optionIndex) => {
    const newQuestions = [...formData.questions];
    newQuestions[questionIndex].options = newQuestions[questionIndex].options.filter((_, index) => index !== optionIndex);
    setFormData({ ...formData, questions: newQuestions });
  };

  const updateQuestion = (questionIndex, field, value) => {
    const newQuestions = [...formData.questions];
    newQuestions[questionIndex][field] = value;
    setFormData({ ...formData, questions: newQuestions });
  };

  const updateOption = (questionIndex, optionIndex, field, value) => {
    const newQuestions = [...formData.questions];
    if (field === 'isCorrect' && value) {
      // Only one correct answer per question
      newQuestions[questionIndex].options.forEach((option, index) => {
        option.isCorrect = index === optionIndex;
      });
    } else {
      newQuestions[questionIndex].options[optionIndex][field] = value;
    }
    setFormData({ ...formData, questions: newQuestions });
  };

  const openEditModal = (qcm) => {
    setSelectedQcm(qcm);
    setFormData({
      title: qcm.title,
      description: qcm.description || '',
      course: qcm.course._id,
      timeLimit: qcm.timeLimit || 60,
      passingScore: qcm.passingScore || 70,
      isActive: qcm.isActive !== undefined ? qcm.isActive : true,
      allowMultipleAttempts: qcm.allowMultipleAttempts || false,
      showResultsImmediately: qcm.showResultsImmediately !== undefined ? qcm.showResultsImmediately : true,
      randomizeQuestions: qcm.randomizeQuestions || false,
      randomizeOptions: qcm.randomizeOptions || false,
      availableFrom: qcm.availableFrom ? new Date(qcm.availableFrom).toISOString().slice(0, 16) : '',
      availableUntil: qcm.availableUntil ? new Date(qcm.availableUntil).toISOString().slice(0, 16) : '',
      instructions: qcm.instructions || '',
      questions: qcm.questions && qcm.questions.length > 0 ? qcm.questions : [{ 
        questionText: '', 
        points: 1,
        options: [
          { text: '', isCorrect: false },
          { text: '', isCorrect: false }
        ] 
      }]
    });
    onEditOpen();
  };

  const openAssignModal = (qcm) => {
    setSelectedQcm(qcm);
    fetchStudentsForCourse(qcm.course._id);
    onAssignOpen();
  };

  const openViewModal = (qcm) => {
    setSelectedQcm(qcm);
    onViewOpen();
  };

  const openStatsModal = (qcm) => {
    setSelectedQcm(qcm);
    fetchQCMStatistics(qcm._id);
    onStatsOpen();
  };

  const openDeleteModal = (qcm) => {
    setSelectedQcm(qcm);
    onDeleteOpen();
  };

  // Question Form Component
  const QuestionForm = ({ question, questionIndex, isEditing = false }) => (
    <Box p={4} border="1px" borderColor={borderColor} borderRadius="md" mb={4} bg={bgColor}>
      <Flex justify="space-between" align="center" mb={3}>
        <Text fontWeight="bold" fontFamily="Tajawal, sans-serif">
          السؤال {questionIndex + 1}
        </Text>
        {formData.questions.length > 1 && (
          <IconButton
            icon={<FaTimes />}
            size="sm"
            colorScheme="red"
            variant="ghost"
            onClick={() => removeQuestion(questionIndex)}
          />
        )}
      </Flex>

      <VStack spacing={3} align="stretch">
        <FormControl>
          <FormLabel fontFamily="Tajawal, sans-serif">نص السؤال</FormLabel>
          <Textarea
            value={question.questionText}
            onChange={(e) => updateQuestion(questionIndex, 'questionText', e.target.value)}
            placeholder="أدخل نص السؤال"
            fontFamily="Tajawal, sans-serif"
          />
        </FormControl>

        <FormControl>
          <FormLabel fontFamily="Tajawal, sans-serif">النقاط</FormLabel>
          <NumberInput
            value={question.points || 1}
            onChange={(value) => updateQuestion(questionIndex, 'points', parseInt(value) || 1)}
            min={1}
            max={10}
          >
            <NumberInputField />
            <NumberInputStepper>
              <NumberIncrementStepper />
              <NumberDecrementStepper />
            </NumberInputStepper>
          </NumberInput>
        </FormControl>

        <Box>
          <Flex justify="space-between" align="center" mb={2}>
            <Text fontWeight="semibold" fontFamily="Tajawal, sans-serif">الخيارات</Text>
            <Button
              size="sm"
              colorScheme="blue"
              variant="ghost"
              onClick={() => addOption(questionIndex)}
            >
              إضافة خيار
            </Button>
          </Flex>

          {question.options.map((option, optionIndex) => (
            <Flex key={optionIndex} gap={2} mb={2} align="center">
              <Checkbox
                isChecked={option.isCorrect}
                onChange={(e) => updateOption(questionIndex, optionIndex, 'isCorrect', e.target.checked)}
                colorScheme="green"
              />
              <Input
                value={option.text}
                onChange={(e) => updateOption(questionIndex, optionIndex, 'text', e.target.value)}
                placeholder={`الخيار ${optionIndex + 1}`}
                fontFamily="Tajawal, sans-serif"
                flex="1"
              />
              {question.options.length > 2 && (
                <IconButton
                  icon={<FaTimes />}
                  size="sm"
                  colorScheme="red"
                  variant="ghost"
                  onClick={() => removeOption(questionIndex, optionIndex)}
                />
              )}
            </Flex>
          ))}
        </Box>
      </VStack>
    </Box>
  );

  if (loading) {
    return (
      <Box dir="rtl">
        <SidebarInstructor/>
        <Container maxW="8xl" py={8}>
          <Flex justify="center" align="center" h="400px">
            <Spinner size="xl" color={primaryColor} />
          </Flex>
        </Container>
      </Box>
    );
  }

  return (
    <Box dir="rtl">
      <SidebarInstructor/>
      <Container maxW="8xl" py={8}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Flex mb={6} align="center">
            <Heading size="lg" color={secondaryColor} fontFamily="Tajawal, sans-serif">
              إدارة الاختبارات
            </Heading>
            <Spacer />
            <Button
              leftIcon={<FaPlus />}
              colorScheme="yellow"
              bg={primaryColor}
              color={secondaryColor}
              onClick={onCreateOpen}
              fontFamily="Tajawal, sans-serif"
            >
              إنشاء اختبار جديد
            </Button>
          </Flex>

          {qcms.length === 0 ? (
            <Alert status="info" borderRadius="lg">
              <AlertIcon />
              <Text fontFamily="Tajawal, sans-serif">لا توجد اختبارات متاحة</Text>
            </Alert>
          ) : (
            <Box bg={bgColor} borderRadius="lg" boxShadow="md" overflow="hidden">
              <Table variant="simple">
                <Thead bg={secondaryColor}>
                  <Tr>
                    <Th color="white" fontFamily="Tajawal, sans-serif">العنوان</Th>
                    <Th color="white" fontFamily="Tajawal, sans-serif">الدورة</Th>
                    <Th color="white" fontFamily="Tajawal, sans-serif">عدد الأسئلة</Th>
                    <Th color="white" fontFamily="Tajawal, sans-serif">الحالة</Th>
                    <Th color="white" fontFamily="Tajawal, sans-serif">تاريخ الإنشاء</Th>
                    <Th color="white" fontFamily="Tajawal, sans-serif">الإجراءات</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {qcms.map((qcm) => (
                    <Tr key={qcm._id} _hover={{ bg: 'gray.50' }}>
                      <Td fontFamily="Tajawal, sans-serif">{qcm.title}</Td>
                      <Td fontFamily="Tajawal, sans-serif">{qcm.course?.title}</Td>
                      <Td fontFamily="Tajawal, sans-serif">{qcm.questions?.length || 0}</Td>
                      <Td>
                        <Badge
                          colorScheme={qcm.isActive ? 'green' : 'red'}
                          fontFamily="Tajawal, sans-serif"
                        >
                          {qcm.isActive ? 'نشط' : 'غير نشط'}
                        </Badge>
                      </Td>
                      <Td fontFamily="Tajawal, sans-serif">
                        {new Date(qcm.createdAt).toLocaleDateString('ar-EG')}
                      </Td>
                      <Td>
                        <Menu>
                          <MenuButton
                            as={IconButton}
                            icon={<FaChevronDown />}
                            variant="ghost"
                            size="sm"
                          />
                          <MenuList>
                            <MenuItem
                              icon={<FaEye />}
                              onClick={() => openViewModal(qcm)}
                              fontFamily="Tajawal, sans-serif"
                            >
                              عرض التفاصيل
                            </MenuItem>
                            <MenuItem
                              icon={<FaEdit />}
                              onClick={() => openEditModal(qcm)}
                              fontFamily="Tajawal, sans-serif"
                            >
                              تعديل
                            </MenuItem>
                            <MenuItem
                              icon={<FaUsers />}
                              onClick={() => openAssignModal(qcm)}
                              fontFamily="Tajawal, sans-serif"
                            >
                              تعيين للطلاب
                            </MenuItem>
                            <MenuItem
                              icon={<FaChartBar />}
                              onClick={() => openStatsModal(qcm)}
                              fontFamily="Tajawal, sans-serif"
                            >
                              عرض الإحصائيات
                            </MenuItem>
                            <Divider />
                            <MenuItem
                              icon={<FaTrash />}
                              onClick={() => openDeleteModal(qcm)}
                              color="red.500"
                              fontFamily="Tajawal, sans-serif"
                            >
                              حذف
                            </MenuItem>
                          </MenuList>
                        </Menu>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          )}
        </motion.div>

        {/* Create QCM Modal */}
        <Modal isOpen={isCreateOpen} onClose={onCreateClose} size="6xl" scrollBehavior="inside">
          <ModalOverlay />
          <ModalContent maxH="90vh">
            <ModalHeader fontFamily="Tajawal, sans-serif">إنشاء اختبار جديد</ModalHeader>
            <ModalCloseButton />
            <ModalBody pb={6}>
              <VStack spacing={6} align="stretch">
                {/* Basic Information */}
                <Box>
                  <Text fontSize="lg" fontWeight="bold" mb={4} fontFamily="Tajawal, sans-serif">
                    المعلومات الأساسية
                  </Text>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                    <FormControl>
                      <FormLabel fontFamily="Tajawal, sans-serif">عنوان الاختبار</FormLabel>
                      <Input
                        value={formData.title}
                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                        placeholder="أدخل عنوان الاختبار"
                        fontFamily="Tajawal, sans-serif"
                      />
                    </FormControl>

                    <FormControl>
                      <FormLabel fontFamily="Tajawal, sans-serif">الدورة</FormLabel>
                      <Select
                        value={formData.course}
                        onChange={(e) => setFormData({ ...formData, course: e.target.value })}
                        placeholder="اختر الدورة"
                        fontFamily="Tajawal, sans-serif"
                      >
                        {courses.map((course) => (
                          <option key={course._id} value={course._id}>
                            {course.name}
                          </option>
                        ))}
                      </Select>
                    </FormControl>
                  </SimpleGrid>

                  <FormControl mt={4}>
                    <FormLabel fontFamily="Tajawal, sans-serif">الوصف</FormLabel>
                    <Textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="أدخل وصف الاختبار"
                      fontFamily="Tajawal, sans-serif"
                    />
                  </FormControl>
                </Box>

                {/* Settings */}
                <Box>
                  <Text fontSize="lg" fontWeight="bold" mb={4} fontFamily="Tajawal, sans-serif">
                    إعدادات الاختبار
                  </Text>
                  <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                    <FormControl>
                      <FormLabel fontFamily="Tajawal, sans-serif">الوقت المحدد (دقيقة)</FormLabel>
                      <NumberInput
                        value={formData.timeLimit}
                        onChange={(value) => setFormData({ ...formData, timeLimit: parseInt(value) || 60 })}
                        min={1}
                        max={300}
                      >
                        <NumberInputField />
                        <NumberInputStepper>
                          <NumberIncrementStepper />
                          <NumberDecrementStepper />
                        </NumberInputStepper>
                      </NumberInput>
                    </FormControl>

                    <FormControl>
                      <FormLabel fontFamily="Tajawal, sans-serif">درجة النجاح (%)</FormLabel>
                      <NumberInput
                        value={formData.passingScore}
                        onChange={(value) => setFormData({ ...formData, passingScore: parseInt(value) || 70 })}
                        min={0}
                        max={100}
                      >
                        <NumberInputField />
                        <NumberInputStepper>
                          <NumberIncrementStepper />
                          <NumberDecrementStepper />
                        </NumberInputStepper>
                      </NumberInput>
                    </FormControl>

                    <FormControl>
                      <FormLabel fontFamily="Tajawal, sans-serif">الحالة</FormLabel>
                      <Switch
                        isChecked={formData.isActive}
                        onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                        colorScheme="green"
                      />
                      <Text fontSize="sm" color="gray.500" fontFamily="Tajawal, sans-serif">
                        {formData.isActive ? 'نشط' : 'غير نشط'}
                      </Text>
                    </FormControl>
                  </SimpleGrid>

                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mt={4}>
                    <FormControl>
                      <FormLabel fontFamily="Tajawal, sans-serif">متاح من</FormLabel>
                      <Input
                        type="datetime-local"
                        value={formData.availableFrom}
                        onChange={(e) => setFormData({ ...formData, availableFrom: e.target.value })}
                      />
                    </FormControl>

                    <FormControl>
                      <FormLabel fontFamily="Tajawal, sans-serif">متاح حتى</FormLabel>
                      <Input
                        type="datetime-local"
                        value={formData.availableUntil}
                        onChange={(e) => setFormData({ ...formData, availableUntil: e.target.value })}
                      />
                    </FormControl>
                  </SimpleGrid>

                  <Stack direction="row" spacing={6} mt={4}>
                    <FormControl display="flex" alignItems="center">
                      <FormLabel htmlFor="multiple-attempts" mb="0" fontFamily="Tajawal, sans-serif">
                        السماح بمحاولات متعددة
                      </FormLabel>
                      <Switch
                        id="multiple-attempts"
                        isChecked={formData.allowMultipleAttempts}
                        onChange={(e) => setFormData({ ...formData, allowMultipleAttempts: e.target.checked })}
                        colorScheme="blue"
                      />
                    </FormControl>

                    <FormControl display="flex" alignItems="center">
                      <FormLabel htmlFor="show-results" mb="0" fontFamily="Tajawal, sans-serif">
                        إظهار النتائج فوراً
                      </FormLabel>
                      <Switch
                        id="show-results"
                        isChecked={formData.showResultsImmediately}
                        onChange={(e) => setFormData({ ...formData, showResultsImmediately: e.target.checked })}
                        colorScheme="green"
                      />
                    </FormControl>
                  </Stack>

                  <Stack direction="row" spacing={6} mt={4}>
                    <FormControl display="flex" alignItems="center">
                      <FormLabel htmlFor="randomize-questions" mb="0" fontFamily="Tajawal, sans-serif">
                        ترتيب عشوائي للأسئلة
                      </FormLabel>
                      <Switch
                        id="randomize-questions"
                        isChecked={formData.randomizeQuestions}
                        onChange={(e) => setFormData({ ...formData, randomizeQuestions: e.target.checked })}
                        colorScheme="purple"
                      />
                    </FormControl>

                    <FormControl display="flex" alignItems="center">
                      <FormLabel htmlFor="randomize-options" mb="0" fontFamily="Tajawal, sans-serif">
                        ترتيب عشوائي للخيارات
                      </FormLabel>
                      <Switch
                        id="randomize-options"
                        isChecked={formData.randomizeOptions}
                        onChange={(e) => setFormData({ ...formData, randomizeOptions: e.target.checked })}
                        colorScheme="orange"
                      />
                    </FormControl>
                  </Stack>
                </Box>

                {/* Instructions */}
                <Box>
                  <FormControl>
                    <FormLabel fontFamily="Tajawal, sans-serif">تعليمات الاختبار</FormLabel>
                    <Textarea
                      value={formData.instructions}
                      onChange={(e) => setFormData({ ...formData, instructions: e.target.value })}
                      placeholder="أدخل تعليمات للطلاب قبل بدء الاختبار"
                      fontFamily="Tajawal, sans-serif"
                      rows={3}
                    />
                  </FormControl>
                </Box>

                {/* Questions */}
                <Box>
                  <Flex justify="space-between" align="center" mb={4}>
                    <Text fontSize="lg" fontWeight="bold" fontFamily="Tajawal, sans-serif">
                      الأسئلة ({formData.questions.length})
                    </Text>
                    <Button
                      leftIcon={<FaPlus />}
                      colorScheme="blue"
                      size="sm"
                      onClick={addQuestion}
                      fontFamily="Tajawal, sans-serif"
                    >
                      إضافة سؤال
                    </Button>
                  </Flex>

                  {formData.questions.map((question, questionIndex) => (
                    <QuestionForm
                      key={questionIndex}
                      question={question}
                      questionIndex={questionIndex}
                    />
                  ))}
                </Box>
              </VStack>
            </ModalBody>

            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={onCreateClose} fontFamily="Tajawal, sans-serif">
                إلغاء
              </Button>
              <Button
                colorScheme="yellow"
                bg={primaryColor}
                color={secondaryColor}
                onClick={handleCreateQCM}
                fontFamily="Tajawal, sans-serif"
              >
                إنشاء الاختبار
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>

        {/* Edit QCM Modal */}
        <Modal isOpen={isEditOpen} onClose={onEditClose} size="6xl" scrollBehavior="inside">
          <ModalOverlay />
          <ModalContent maxH="90vh">
            <ModalHeader fontFamily="Tajawal, sans-serif">تعديل الاختبار</ModalHeader>
            <ModalCloseButton />
            <ModalBody pb={6}>
              <VStack spacing={6} align="stretch">
                {/* Basic Information */}
                <Box>
                  <Text fontSize="lg" fontWeight="bold" mb={4} fontFamily="Tajawal, sans-serif">
                    المعلومات الأساسية
                  </Text>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                    <FormControl>
                      <FormLabel fontFamily="Tajawal, sans-serif">عنوان الاختبار</FormLabel>
                      <Input
                        value={formData.title}
                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                        placeholder="أدخل عنوان الاختبار"
                        fontFamily="Tajawal, sans-serif"
                      />
                    </FormControl>

                    <FormControl>
                      <FormLabel fontFamily="Tajawal, sans-serif">الدورة</FormLabel>
                      <Select
                        value={formData.course}
                        onChange={(e) => setFormData({ ...formData, course: e.target.value })}
                        placeholder="اختر الدورة"
                        fontFamily="Tajawal, sans-serif"
                      >
                        {courses.map((course) => (
                          <option key={course._id} value={course._id}>
                            {course.name}
                          </option>
                        ))}
                      </Select>
                    </FormControl>
                  </SimpleGrid>

                  <FormControl mt={4}>
                    <FormLabel fontFamily="Tajawal, sans-serif">الوصف</FormLabel>
                    <Textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="أدخل وصف الاختبار"
                      fontFamily="Tajawal, sans-serif"
                    />
                  </FormControl>
                </Box>

                {/* Settings */}
                <Box>
                  <Text fontSize="lg" fontWeight="bold" mb={4} fontFamily="Tajawal, sans-serif">
                    إعدادات الاختبار
                  </Text>
                  <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                    <FormControl>
                      <FormLabel fontFamily="Tajawal, sans-serif">الوقت المحدد (دقيقة)</FormLabel>
                      <NumberInput
                        value={formData.timeLimit}
                        onChange={(value) => setFormData({ ...formData, timeLimit: parseInt(value) || 60 })}
                        min={1}
                        max={300}
                      >
                        <NumberInputField />
                        <NumberInputStepper>
                          <NumberIncrementStepper />
                          <NumberDecrementStepper />
                        </NumberInputStepper>
                      </NumberInput>
                    </FormControl>

                    <FormControl>
                      <FormLabel fontFamily="Tajawal, sans-serif">درجة النجاح (%)</FormLabel>
                      <NumberInput
                        value={formData.passingScore}
                        onChange={(value) => setFormData({ ...formData, passingScore: parseInt(value) || 70 })}
                        min={0}
                        max={100}
                      >
                        <NumberInputField />
                        <NumberInputStepper>
                          <NumberIncrementStepper />
                          <NumberDecrementStepper />
                        </NumberInputStepper>
                      </NumberInput>
                    </FormControl>

                    <FormControl>
                      <FormLabel fontFamily="Tajawal, sans-serif">الحالة</FormLabel>
                      <Switch
                        isChecked={formData.isActive}
                        onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                        colorScheme="green"
                      />
                      <Text fontSize="sm" color="gray.500" fontFamily="Tajawal, sans-serif">
                        {formData.isActive ? 'نشط' : 'غير نشط'}
                      </Text>
                    </FormControl>
                  </SimpleGrid>

                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mt={4}>
                    <FormControl>
                      <FormLabel fontFamily="Tajawal, sans-serif">متاح من</FormLabel>
                      <Input
                        type="datetime-local"
                        value={formData.availableFrom}
                        onChange={(e) => setFormData({ ...formData, availableFrom: e.target.value })}
                      />
                    </FormControl>

                    <FormControl>
                      <FormLabel fontFamily="Tajawal, sans-serif">متاح حتى</FormLabel>
                      <Input
                        type="datetime-local"
                        value={formData.availableUntil}
                        onChange={(e) => setFormData({ ...formData, availableUntil: e.target.value })}
                      />
                    </FormControl>
                  </SimpleGrid>

                  <Stack direction="row" spacing={6} mt={4}>
                    <FormControl display="flex" alignItems="center">
                      <FormLabel htmlFor="edit-multiple-attempts" mb="0" fontFamily="Tajawal, sans-serif">
                        السماح بمحاولات متعددة
                      </FormLabel>
                      <Switch
                        id="edit-multiple-attempts"
                        isChecked={formData.allowMultipleAttempts}
                        onChange={(e) => setFormData({ ...formData, allowMultipleAttempts: e.target.checked })}
                        colorScheme="blue"
                      />
                    </FormControl>

                    <FormControl display="flex" alignItems="center">
                      <FormLabel htmlFor="edit-show-results" mb="0" fontFamily="Tajawal, sans-serif">
                        إظهار النتائج فوراً
                      </FormLabel>
                      <Switch
                        id="edit-show-results"
                        isChecked={formData.showResultsImmediately}
                        onChange={(e) => setFormData({ ...formData, showResultsImmediately: e.target.checked })}
                        colorScheme="green"
                      />
                    </FormControl>
                  </Stack>

                  <Stack direction="row" spacing={6} mt={4}>
                    <FormControl display="flex" alignItems="center">
                      <FormLabel htmlFor="edit-randomize-questions" mb="0" fontFamily="Tajawal, sans-serif">
                        ترتيب عشوائي للأسئلة
                      </FormLabel>
                      <Switch
                        id="edit-randomize-questions"
                        isChecked={formData.randomizeQuestions}
                        onChange={(e) => setFormData({ ...formData, randomizeQuestions: e.target.checked })}
                        colorScheme="purple"
                      />
                    </FormControl>

                    <FormControl display="flex" alignItems="center">
                      <FormLabel htmlFor="edit-randomize-options" mb="0" fontFamily="Tajawal, sans-serif">
                        ترتيب عشوائي للخيارات
                      </FormLabel>
                      <Switch
                        id="edit-randomize-options"
                        isChecked={formData.randomizeOptions}
                        onChange={(e) => setFormData({ ...formData, randomizeOptions: e.target.checked })}
                        colorScheme="orange"
                      />
                    </FormControl>
                  </Stack>
                </Box>

                {/* Instructions */}
                <Box>
                  <FormControl>
                    <FormLabel fontFamily="Tajawal, sans-serif">تعليمات الاختبار</FormLabel>
                    <Textarea
                      value={formData.instructions}
                      onChange={(e) => setFormData({ ...formData, instructions: e.target.value })}
                      placeholder="أدخل تعليمات للطلاب قبل بدء الاختبار"
                      fontFamily="Tajawal, sans-serif"
                      rows={3}
                    />
                  </FormControl>
                </Box>

                {/* Questions */}
                <Box>
                  <Flex justify="space-between" align="center" mb={4}>
                    <Text fontSize="lg" fontWeight="bold" fontFamily="Tajawal, sans-serif">
                      الأسئلة ({formData.questions.length})
                    </Text>
                    <Button
                      leftIcon={<FaPlus />}
                      colorScheme="blue"
                      size="sm"
                      onClick={addQuestion}
                      fontFamily="Tajawal, sans-serif"
                    >
                      إضافة سؤال
                    </Button>
                  </Flex>

                  {formData.questions.map((question, questionIndex) => (
                    <QuestionForm
                      key={questionIndex}
                      question={question}
                      questionIndex={questionIndex}
                      isEditing={true}
                    />
                  ))}
                </Box>
              </VStack>
            </ModalBody>

            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={onEditClose} fontFamily="Tajawal, sans-serif">
                إلغاء
              </Button>
              <Button
                colorScheme="yellow"
                bg={primaryColor}
                color={secondaryColor}
                onClick={handleUpdateQCM}
                fontFamily="Tajawal, sans-serif"
              >
                حفظ التغييرات
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>

        {/* View QCM Details Modal */}
        <Modal isOpen={isViewOpen} onClose={onViewClose} size="4xl" scrollBehavior="inside">
          <ModalOverlay />
          <ModalContent maxH="90vh">
            <ModalHeader fontFamily="Tajawal, sans-serif">تفاصيل الاختبار</ModalHeader>
            <ModalCloseButton />
            <ModalBody pb={6}>
              {selectedQcm && (
                <VStack spacing={6} align="stretch">
                  {/* Basic Info */}
                  <Box p={4} bg="gray.50" borderRadius="md">
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                      <Box>
                        <Text fontWeight="bold" fontFamily="Tajawal, sans-serif">العنوان:</Text>
                        <Text fontFamily="Tajawal, sans-serif">{selectedQcm.title}</Text>
                      </Box>
                      <Box>
                        <Text fontWeight="bold" fontFamily="Tajawal, sans-serif">الدورة:</Text>
                        <Text fontFamily="Tajawal, sans-serif">{selectedQcm.course?.title}</Text>
                      </Box>
                      <Box>
                        <Text fontWeight="bold" fontFamily="Tajawal, sans-serif">الوقت المحدد:</Text>
                        <Text fontFamily="Tajawal, sans-serif">{selectedQcm.timeLimit} دقيقة</Text>
                      </Box>
                      <Box>
                        <Text fontWeight="bold" fontFamily="Tajawal, sans-serif">درجة النجاح:</Text>
                        <Text fontFamily="Tajawal, sans-serif">{selectedQcm.passingScore}%</Text>
                      </Box>
                      <Box>
                        <Text fontWeight="bold" fontFamily="Tajawal, sans-serif">الحالة:</Text>
                        <Badge colorScheme={selectedQcm.isActive ? 'green' : 'red'}>
                          {selectedQcm.isActive ? 'نشط' : 'غير نشط'}
                        </Badge>
                      </Box>
                      <Box>
                        <Text fontWeight="bold" fontFamily="Tajawal, sans-serif">عدد الأسئلة:</Text>
                        <Text fontFamily="Tajawal, sans-serif">{selectedQcm.questions?.length || 0}</Text>
                      </Box>
                    </SimpleGrid>

                    {selectedQcm.description && (
                      <Box mt={4}>
                        <Text fontWeight="bold" fontFamily="Tajawal, sans-serif">الوصف:</Text>
                        <Text fontFamily="Tajawal, sans-serif">{selectedQcm.description}</Text>
                      </Box>
                    )}

                    {selectedQcm.instructions && (
                      <Box mt={4}>
                        <Text fontWeight="bold" fontFamily="Tajawal, sans-serif">التعليمات:</Text>
                        <Text fontFamily="Tajawal, sans-serif">{selectedQcm.instructions}</Text>
                      </Box>
                    )}
                  </Box>

                  {/* Questions */}
                  {selectedQcm.questions && selectedQcm.questions.length > 0 && (
                    <Box>
                      <Text fontSize="lg" fontWeight="bold" mb={4} fontFamily="Tajawal, sans-serif">
                        الأسئلة
                      </Text>
                      <Accordion allowMultiple>
                        {selectedQcm.questions.map((question, index) => (
                          <AccordionItem key={index}>
                            <AccordionButton>
                              <Box flex="1" textAlign="right">
                                <Text fontFamily="Tajawal, sans-serif">
                                  السؤال {index + 1}: {question.questionText}
                                </Text>
                              </Box>
                              <AccordionIcon />
                            </AccordionButton>
                            <AccordionPanel pb={4}>
                              <VStack align="stretch" spacing={2}>
                                {question.options?.map((option, optionIndex) => (
                                  <Flex key={optionIndex} align="center" gap={2}>
                                    <Box
                                      w={4}
                                      h={4}
                                      borderRadius="full"
                                      bg={option.isCorrect ? 'green.500' : 'gray.300'}
                                    />
                                    <Text fontFamily="Tajawal, sans-serif">{option.text}</Text>
                                    {option.isCorrect && (
                                      <Badge colorScheme="green" size="sm">صحيح</Badge>
                                    )}
                                  </Flex>
                                ))}
                                <Text fontSize="sm" color="gray.500" fontFamily="Tajawal, sans-serif">
                                  النقاط: {question.points || 1}
                                </Text>
                              </VStack>
                            </AccordionPanel>
                          </AccordionItem>
                        ))}
                      </Accordion>
                    </Box>
                  )}
                </VStack>
              )}
            </ModalBody>

            <ModalFooter>
              <Button onClick={onViewClose} fontFamily="Tajawal, sans-serif">
                إغلاق
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>

        {/* Assign to Students Modal */}
        <Modal isOpen={isAssignOpen} onClose={onAssignClose} size="lg">
          <ModalOverlay />
          <ModalContent>
            <ModalHeader fontFamily="Tajawal, sans-serif">تعيين الاختبار للطلاب</ModalHeader>
            <ModalCloseButton />
            <ModalBody pb={6}>
              {selectedQcm && (
                <VStack spacing={4} align="stretch">
                  <Box p={4} bg="blue.50" borderRadius="md">
                    <Text fontWeight="bold" fontFamily="Tajawal, sans-serif">
                      الاختبار: {selectedQcm.title}
                    </Text>
                    <Text fontSize="sm" color="gray.600" fontFamily="Tajawal, sans-serif">
                      الدورة: {selectedQcm.course?.title}
                    </Text>
                  </Box>

                  <FormControl>
                    <FormLabel fontFamily="Tajawal, sans-serif">اختر الطلاب</FormLabel>
                    <Box maxH="300px" overflowY="auto" border="1px" borderColor="gray.200" borderRadius="md" p={3}>
                      <CheckboxGroup
                        value={selectedStudents}
                        onChange={setSelectedStudents}
                      >
                        <Stack spacing={2}>
                          {students.map((student) => (
                            <Checkbox key={student._id} value={student._id} fontFamily="Tajawal, sans-serif">
                              {student.name} ({student.email})
                            </Checkbox>
                          ))}
                        </Stack>
                      </CheckboxGroup>
                    </Box>
                    <Text fontSize="sm" color="gray.500" mt={2} fontFamily="Tajawal, sans-serif">
                      تم اختيار {selectedStudents.length} طالب
                    </Text>
                  </FormControl>
                </VStack>
              )}
            </ModalBody>

            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={onAssignClose} fontFamily="Tajawal, sans-serif">
                إلغاء
              </Button>
              <Button
                colorScheme="blue"
                onClick={handleAssignQCM}
                isDisabled={selectedStudents.length === 0}
                fontFamily="Tajawal, sans-serif"
              >
                تعيين الاختبار
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>

        {/* Statistics Modal */}
        <Modal isOpen={isStatsOpen} onClose={onStatsClose} size="6xl" scrollBehavior="inside">
          <ModalOverlay />
          <ModalContent maxH="90vh">
            <ModalHeader fontFamily="Tajawal, sans-serif">إحصائيات الاختبار</ModalHeader>
            <ModalCloseButton />
            <ModalBody pb={6}>
              {selectedQcm && statistics && (
                <VStack spacing={6} align="stretch">
                  {/* Overview Stats */}
                  <Box>
                    <Text fontSize="lg" fontWeight="bold" mb={4} fontFamily="Tajawal, sans-serif">
                      نظرة عامة - {selectedQcm.title}
                    </Text>
                    <SimpleGrid columns={{ base: 1, md: 4 }} spacing={4}>
                      <Stat>
                        <StatLabel fontFamily="Tajawal, sans-serif">إجمالي الطلاب المعينين</StatLabel>
                        <StatNumber>{statistics.students?.length || 0}</StatNumber>
                        <StatHelpText fontFamily="Tajawal, sans-serif">
                          <FaUsers style={{ display: 'inline', marginLeft: '4px' }} />
                          طالب
                        </StatHelpText>
                      </Stat>

                      <Stat>
                        <StatLabel fontFamily="Tajawal, sans-serif">إجمالي المحاولات</StatLabel>
                        <StatNumber>{statistics.attempts?.length || 0}</StatNumber>
                        <StatHelpText fontFamily="Tajawal, sans-serif">
                          <FaEdit style={{ display: 'inline', marginLeft: '4px' }} />
                          محاولة
                        </StatHelpText>
                      </Stat>

                      <Stat>
                        <StatLabel fontFamily="Tajawal, sans-serif">معدل النجاح</StatLabel>
                        <StatNumber>
                          {statistics.attempts?.length > 0 
                            ? Math.round((statistics.attempts.filter(a => a.passed).length / statistics.attempts.length) * 100)
                            : 0}%
                        </StatNumber>
                        <StatHelpText fontFamily="Tajawal, sans-serif">
                          <FaCheck style={{ display: 'inline', marginLeft: '4px' }} />
                          نجح
                        </StatHelpText>
                      </Stat>

                      <Stat>
                        <StatLabel fontFamily="Tajawal, sans-serif">متوسط الدرجات</StatLabel>
                        <StatNumber>
                          {statistics.attempts?.length > 0 
                            ? Math.round(statistics.attempts.reduce((sum, a) => sum + (a.percentage || 0), 0) / statistics.attempts.length)
                            : 0}%
                        </StatNumber>
                        <StatHelpText fontFamily="Tajawal, sans-serif">
                          <FaChartBar style={{ display: 'inline', marginLeft: '4px' }} />
                          متوسط
                        </StatHelpText>
                      </Stat>
                    </SimpleGrid>
                  </Box>

                  {/* Students Performance */}
                  <Box>
                    <Text fontSize="lg" fontWeight="bold" mb={4} fontFamily="Tajawal, sans-serif">
                      أداء الطلاب
                    </Text>
                    <Box bg={bgColor} borderRadius="lg" overflow="hidden" border="1px" borderColor={borderColor}>
                      <Table variant="simple" size="sm">
                        <Thead bg="gray.50">
                          <Tr>
                            <Th fontFamily="Tajawal, sans-serif">الطالب</Th>
                            <Th fontFamily="Tajawal, sans-serif">عدد المحاولات</Th>
                            <Th fontFamily="Tajawal, sans-serif">أفضل درجة</Th>
                            <Th fontFamily="Tajawal, sans-serif">الحالة</Th>
                            <Th fontFamily="Tajawal, sans-serif">آخر محاولة</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {statistics.students?.map((studentStat) => (
                            <Tr key={studentStat.student._id}>
                              <Td fontFamily="Tajawal, sans-serif">{studentStat.student.name}</Td>
                              <Td fontFamily="Tajawal, sans-serif">{studentStat.attemptCount}</Td>
                              <Td fontFamily="Tajawal, sans-serif">
                                {studentStat.bestScore !== null ? `${studentStat.bestScore}%` : '-'}
                              </Td>
                              <Td>
                                <Badge
                                  colorScheme={
                                    studentStat.bestScore >= selectedQcm.passingScore ? 'green' : 
                                    studentStat.attemptCount > 0 ? 'red' : 'gray'
                                  }
                                  fontFamily="Tajawal, sans-serif"
                                >
                                  {studentStat.bestScore >= selectedQcm.passingScore ? 'نجح' : 
                                   studentStat.attemptCount > 0 ? 'لم ينجح' : 'لم يبدأ'}
                                </Badge>
                              </Td>
                              <Td fontFamily="Tajawal, sans-serif">
                                {studentStat.lastAttempt 
                                  ? new Date(studentStat.lastAttempt).toLocaleDateString('ar-EG')
                                  : '-'
                                }
                              </Td>
                            </Tr>
                          ))}
                        </Tbody>
                      </Table>
                    </Box>
                  </Box>

                  {/* Score Distribution */}
                  {statistics.attempts?.length > 0 && (
                    <Box>
                      <Text fontSize="lg" fontWeight="bold" mb={4} fontFamily="Tajawal, sans-serif">
                        توزيع الدرجات
                      </Text>
                      <SimpleGrid columns={{ base: 1, md: 5 }} spacing={4}>
                        {[
                          { range: '90-100%', color: 'green', count: statistics.attempts.filter(a => a.percentage >= 90).length },
                          { range: '80-89%', color: 'blue', count: statistics.attempts.filter(a => a.percentage >= 80 && a.percentage < 90).length },
                          { range: '70-79%', color: 'yellow', count: statistics.attempts.filter(a => a.percentage >= 70 && a.percentage < 80).length },
                          { range: '60-69%', color: 'orange', count: statistics.attempts.filter(a => a.percentage >= 60 && a.percentage < 70).length },
                          { range: '0-59%', color: 'red', count: statistics.attempts.filter(a => a.percentage < 60).length }
                        ].map((range, index) => (
                          <Box key={index} p={4} bg="gray.50" borderRadius="md" textAlign="center">
                            <Text fontWeight="bold" fontFamily="Tajawal, sans-serif">{range.range}</Text>
                            <Text fontSize="2xl" fontWeight="bold" color={`${range.color}.500`}>
                              {range.count}
                            </Text>
                            <Progress
                              value={statistics.attempts.length > 0 ? (range.count / statistics.attempts.length) * 100 : 0}
                              colorScheme={range.color}
                              size="sm"
                              mt={2}
                            />
                          </Box>
                        ))}
                      </SimpleGrid>
                    </Box>
                  )}
                </VStack>
              )}
            </ModalBody>

            <ModalFooter>
              <Button onClick={onStatsClose} fontFamily="Tajawal, sans-serif">
                إغلاق
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>

        {/* Delete Confirmation Modal */}
        <AlertDialog
          isOpen={isDeleteOpen}
          onClose={onDeleteClose}
          isCentered
        >
          <AlertDialogOverlay>
            <AlertDialogContent>
              <AlertDialogHeader fontSize="lg" fontWeight="bold" fontFamily="Tajawal, sans-serif">
                تأكيد الحذف
              </AlertDialogHeader>

              <AlertDialogBody fontFamily="Tajawal, sans-serif">
                هل أنت متأكد من حذف الاختبار "{selectedQcm?.title}"؟
                <br />
                <Text color="red.500" fontSize="sm" mt={2}>
                  تحذير: سيتم حذف جميع المحاولات والنتائج المرتبطة بهذا الاختبار نهائياً.
                </Text>
              </AlertDialogBody>

              <AlertDialogFooter>
                <Button onClick={onDeleteClose} fontFamily="Tajawal, sans-serif">
                  إلغاء
                </Button>
                <Button colorScheme="red" onClick={handleDeleteQCM} mr={3} fontFamily="Tajawal, sans-serif">
                  حذف
                </Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialogOverlay>
        </AlertDialog>
      </Container>
    </Box>
  );
};

export default QCMManagementPage;


